import React, {useEffect, useMemo, useState} from "react";
import {UAParser} from "ua-parser-js";
import {Button, Grid, IconButton, Typography, useMediaQuery} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import {PortTheme} from "~/ui/theme";
import {Close, LogoVertical} from "~/ui/icons";
import {useHistory} from "react-router-dom";
import MobileDetect from "mobile-detect";

const useStyles = makeStyles<PortTheme>(theme => ({
    root: {
        padding: "10px",
        backgroundColor: "#D7E2EE",
        borderRadius: 8,
        margin: "0 16px",
        boxShadow: "0px 4px 24px rgba(104, 117, 130, 0.09)"
    },
    logo: {
        backgroundColor: "#FFFFFF",
        borderRadius: 8,
        width: 48,
        height: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiSvgIcon-root": {
            fontSize: "36px",
        }
    },
    installButton: {
        height: 24,
        width: 140,
    },
    closeButton: {
        marginTop: "-8px",
        marginRight: "-8px",
    }
}));


interface BeforeInstallPromptEvent extends Event {
    readonly platforms: Array<string>;

    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed',
        platform: string
    }>;

    prompt(): Promise<{
        outcome: 'accepted' | 'dismissed',
        platform: string
    }>;
}


const INSTALL_PROMPT_GLOBAL_VAR_NAME = "_installPromptEvent";


export const InstallPrompt = () => {
    const history = useHistory();
    const classes = useStyles();
    const [nativePrompt, setNativePrompt] = useState<BeforeInstallPromptEvent>(window[INSTALL_PROMPT_GLOBAL_VAR_NAME]);
    const [showInstallPrompt, setShowInstallPrompt] = useState(false);

    const isMobile = useMemo(() => {
        return typeof window != "undefined" && new MobileDetect(window.navigator.userAgent).mobile();
    }, []);

    useEffect(() => {
        window[INSTALL_PROMPT_GLOBAL_VAR_NAME] = nativePrompt;
    }, [nativePrompt])

    useEffect(() => {
        if (typeof window == "undefined") {
            return;
        }
        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    }, []);

    // browser native prompt enable
    useEffect(() => {
        const showValueInStorage = JSON.parse(sessionStorage.getItem("hide-install-prompt"));
        if (nativePrompt && !showValueInStorage) {
            setShowInstallPrompt(true);
        }
    }, [nativePrompt]);

    useEffect(() => {
        const os = new UAParser().getOS().name.toLowerCase();
        // @ts-ignore
        const standalone = typeof window != "undefined" && window?.navigator?.standalone;
        const showValueInStorage = JSON.parse(sessionStorage.getItem("hide-install-prompt"));
        if (os == "ios" && !standalone && !showValueInStorage) {
            setShowInstallPrompt(true);
        }
        return () => {
            sessionStorage.setItem("hide-install-prompt", "true");
        }
    }, []);

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
        e.preventDefault();
        setNativePrompt(e);
    }

    const handleInstallClick = () => {
        if (nativePrompt) {
            nativePrompt.prompt().then(result => {
                if (result.outcome == "accepted") {
                    setShowInstallPrompt(null);
                    sessionStorage.setItem("hide-install-prompt", "true");
                }
            });
        } else {
            history.push("instructions/install");
        }
    }

    const handleCloseClick = () => {
        setShowInstallPrompt(false);
        sessionStorage.setItem("hide-install-prompt", "true");
    }

    if (!showInstallPrompt || !isMobile) {
        return null;
    }

    return (
        <div className={classes.root}>
            <Grid container wrap="nowrap" columnSpacing="8px">
                <Grid item>
                    <div className={classes.logo}>
                        <LogoVertical/>
                    </div>
                </Grid>
                <Grid item container direction="column" flexGrow={1}>
                    <Typography variant="cardMenuTitle" color="primary" display="block" marginBottom="8px">
                        «Порт в кармане» на главный экран
                    </Typography>
                    <Button color="primary" size="small" onClick={handleInstallClick} className={classes.installButton}>
                        Установить
                    </Button>
                </Grid>
                <Grid item>
                    <IconButton onClick={handleCloseClick} className={classes.closeButton} color="primary">
                        <Close/>
                    </IconButton>
                </Grid>
            </Grid>
        </div>
    );
}
